/**
 * Tonal ramp generation.
 *
 * Every brand colour in the design system expands to an 11-step ramp:
 * 500 is the base, 50-400 are tints toward white, 600-950 are shades toward
 * black. Generating them beats pasting 110 hex values because a partner only
 * ever states its base colours — the ramp cannot drift out of sync with them.
 *
 * The output is verified against the Figma table in themes/tokens/ramp.test.ts
 * (all 110 swatches, exact).
 */

export const RAMP_STEPS = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950
] as const;

export type TRampStep = (typeof RAMP_STEPS)[number];
export type TColorRamp = Record<TRampStep, string>;

/**
 * Fraction of the base colour retained at each step; the remainder is white
 * for tints and black for shades.
 *
 * These are written as keep-factors rather than mix amounts on purpose.
 * Deriving them (`1 - 0.9`) yields 0.09999999999999998, which rounds four of
 * the 950 swatches down by one and breaks parity with Figma.
 */
const KEEP_TINT: Record<number, number> = {
  50: 0.1,
  100: 0.2,
  200: 0.4,
  300: 0.6,
  400: 0.8
};
const KEEP_SHADE: Record<number, number> = {
  600: 0.8,
  700: 0.6,
  800: 0.4,
  900: 0.2,
  950: 0.1
};

/**
 * Parses the colour formats that actually appear in the partner palettes:
 * `#RGB`, `#RRGGBB`, `#RRGGBBAA`, `rgb(r, g, b)` and `rgba(r, g, b, a)`.
 *
 * Alpha is deliberately dropped — a tonal ramp mixes toward opaque white and
 * black, so carrying a partial alpha through it produces colours that do not
 * match anything in Figma. Every base in use today is fully opaque.
 */
const toRgb = (color: string): [number, number, number] => {
  const c = color.trim();

  const fn = c.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const [r, g, b] = fn[1].split(',').map((v) => parseInt(v.trim(), 10));
    return [r, g, b];
  }

  const h = c.replace('#', '');
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16)
    ];
  }
  // 6 or 8 digits — the trailing alpha pair, if present, is ignored.
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
};

const toHex = (rgb: number[]): string =>
  `#${rgb
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/**
 * Expands a base hex colour into its full 11-step ramp.
 *
 * @param base `#RRGGBB` — becomes step 500.
 */
export const buildRamp = (base: string): TColorRamp => {
  const rgb = toRgb(base);

  const at = (step: TRampStep): string => {
    if (step in KEEP_TINT) {
      const k = KEEP_TINT[step];
      return toHex(rgb.map((v) => Math.round(v * k + 255 * (1 - k))));
    }
    if (step in KEEP_SHADE) {
      const k = KEEP_SHADE[step];
      return toHex(rgb.map((v) => Math.round(v * k)));
    }
    return toHex(rgb);
  };

  return Object.fromEntries(
    RAMP_STEPS.map((step) => [step, at(step)])
  ) as TColorRamp;
};

/**
 * The brand gradient every product colour uses:
 * `linear-gradient(176deg, <400> 0%, <500> 33%, <700> 100%)`.
 */
export const buildGradient = (ramp: TColorRamp): string =>
  `linear-gradient(176deg, ${ramp[400]} 0%, ${ramp[500]} 33%, ${ramp[700]} 100%)`;

/**
 * Alpha-composites `overlay` over an opaque `base`, both resolved colours in,
 * and returns the opaque result.
 *
 * Exists for `tintedSurface` (see palette.ts): a light-mode "selected /
 * tinted" surface is just a light ramp step (50-200 — a tint toward white
 * reads fine on a white page), but that trick cannot produce a *dark*
 * tinted surface — every ramp step is either a tint toward white or a shade
 * toward black, never "dark but still recognisably the product's hue".
 * Compositing the product colour at low opacity over the dark card surface
 * is the only way to get both at once.
 */
export const mixOver = (
  base: string,
  overlay: string,
  amount: number
): string => {
  const b = toRgb(base);
  const o = toRgb(overlay);
  return toHex(b.map((v, i) => Math.round(v * (1 - amount) + o[i] * amount)));
};
